/*
=============================================================================
BKNJ GLOBAL CART SYSTEM
File to create in Shopify: assets/bknj-global-cart.js

Purpose:
- One behavior-only cart controller for BKNJ custom desktop/mobile drawers.
- All cart presentation CSS lives in snippets/bknj-global-cart.liquid.
- Keeps Ella original cart code untouched by using only BKNJ selectors/namespaces.
- Handles:
  1) open/close custom cart drawers
  2) fetch/render /cart.js
  3) quantity changes/removes
  4) cart count/subtotal updates
  5) checkout button visibility
  6) product form add-to-cart handoff
  7) generic post-add hooks for independent merchandising modules

Required drawer targets already in your custom headers:
- Desktop: #bknj-cart-drawer-d
- Mobile:  #bknj-cart-drawer

Recommended global snippet later:
- snippets/bknj-global-cart.liquid should create:
  {{ 'bknj-global-cart.js' | asset_url | script_tag }}
=============================================================================
*/

(function(window, document){
  'use strict';

  if (window.BKNJCart && window.BKNJCart.version) return;

  var BKNJCart = {};
  BKNJCart.version = '2.1.0-preloaded-cart-official-payment-icons';

  var selectors = {
    desktopDrawer: '#bknj-cart-drawer-d',
    mobileDrawer: '#bknj-cart-drawer',
    drawers: '#bknj-cart-drawer-d, #bknj-cart-drawer',
    openers: '.js-open-cart',
    closers: '.js-close-cart',
    items: '[data-cart-items]',
    count: '[data-cart-count]',
    titleCount: '[data-cart-title-count]',
    subtotalCount: '[data-cart-subtotal-count]',
    subtotal: '#bknj-cart-subtotal-d, #bknj-cart-subtotal',
    footer: '.bknj-cart__footer, [data-cart-footer], .cart-drawer__footer, .drawer__footer, .cart__footer',
    checkout: 'a[href="/checkout"], a[href*="/checkout"], button[name="checkout"], input[name="checkout"], [data-cart-checkout], .bknj-cart__btn--primary',
    productSection: '[data-product-id]',
    productForm: 'form[action*="/cart/add"], form[action="/cart/add"]',
  };


  var state = {
    activeDrawer: null,
    busy: false,
    openTimer: null,
    cartCache: null,
    cartCacheAt: 0,
    emptyToastTimer: null
  };

  var afterProductAddHooks = [];

  function registerAfterProductAddHook(fn){
    if (typeof fn !== 'function') return function(){};
    if (afterProductAddHooks.indexOf(fn) === -1) afterProductAddHooks.push(fn);
    return function(){
      var index = afterProductAddHooks.indexOf(fn);
      if (index >= 0) afterProductAddHooks.splice(index, 1);
    };
  }

  function runAfterProductAddHooks(context){
    var chain = Promise.resolve(context || {});
    afterProductAddHooks.slice().forEach(function(fn){
      chain = chain.then(function(current){
        return Promise.resolve(fn(current)).then(function(){ return current; });
      });
    });
    return chain;
  }

  function qs(selector, root){
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root){
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function closest(el, selector){
    return el && el.closest ? el.closest(selector) : null;
  }

  function emit(name, detail){
    document.dispatchEvent(new CustomEvent('bknj:cart:' + name, { detail: detail || {} }));
  }

  function rememberCart(cart){
    if (!cart) return cart;
    state.cartCache = cart;
    state.cartCacheAt = Date.now();
    updateBadges(cart);
    return cart;
  }

  function getCachedCart(){
    return state.cartCache || null;
  }

  function isCartCacheFresh(maxAgeMs){
    if (!state.cartCache || !state.cartCacheAt) return false;
    return (Date.now() - state.cartCacheAt) <= Number(maxAgeMs || 15000);
  }

  function cartHasItems(cart){
    return !!(cart && Number(cart.item_count || 0) > 0 && (cart.items || []).length > 0);
  }

  function showEmptyBagMessage(){
    var toast = qs('[data-bknj-cart-empty-toast]');

    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'bknj-jh-notify-toast bknj-cart-empty-toast';
      toast.setAttribute('data-bknj-cart-empty-toast', '');
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.hidden = true;
      document.body.appendChild(toast);
    }

    toast.textContent = 'No items in your bag';
    toast.hidden = false;

    window.clearTimeout(state.emptyToastTimer);
    window.requestAnimationFrame(function(){
      toast.classList.add('show');
    });

    state.emptyToastTimer = window.setTimeout(function(){
      toast.classList.remove('show');
      window.setTimeout(function(){
        toast.hidden = true;
      }, 460);
    }, 2600);
  }

  function escapeHtml(value){
    return String(value == null ? '' : value).replace(/[&<>"']/g, function(character){
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[character];
    });
  }

  function formatMoney(cents){
    cents = Number(cents || 0);

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var format = window.BKNJ_CART_MONEY_FORMAT || window.money_format || window.theme && window.theme.moneyFormat;
      if (format) return window.Shopify.formatMoney(cents, format);
    }

    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';

    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: currency
      });
    } catch(error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function isElementVisible(el){
    if (!el) return false;
    var style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0;
  }

  function isDesktop(){
    return window.matchMedia('(min-width: 1025px)').matches;
  }

  function getDrawer(preferVisible){
    var candidates = qsa(selectors.drawers);

    if (preferVisible) {
      var visible = candidates.find(function(drawer){
        return drawer.getAttribute('aria-hidden') === 'false' || drawer.classList.contains('is-open') || drawer.classList.contains('active');
      });
      if (visible) return visible;
    }

    if (isDesktop()) return qs(selectors.desktopDrawer) || qs(selectors.mobileDrawer);
    return qs(selectors.mobileDrawer) || qs(selectors.desktopDrawer);
  }

  function getAllDrawers(){
    return qsa(selectors.drawers);
  }

  function fetchCart(){
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function(response){
      if (!response.ok) throw new Error('Cart fetch failed');
      return response.json().then(function(cart){ return rememberCart(cart); });
    });
  }

  function changeLine(line, quantity){
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        line: Number(line),
        quantity: Number(quantity)
      })
    }).then(function(response){
      if (!response.ok) {
        return response.json().then(function(error){ throw error; });
      }
      return response.json().then(function(cart){ return rememberCart(cart); });
    });
  }

  function addForm(form){
    var formData = new FormData(form);

    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
      body: formData
    }).then(function(response){
      if (!response.ok) {
        return response.json().then(function(error){ throw error; });
      }
      return response.json();
    });
  }

  function cartMetaHtml(item){
    var rows = [];
    var options = Array.isArray(item && item.options_with_values) ? item.options_with_values : [];

    if (options.length) {
      options.forEach(function(option){
        if (!option || option.value == null) return;
        var name = String(option.name || '').trim();
        var value = String(option.value || '').trim();
        if (!value || value === 'Default Title') return;

        if (/size/i.test(name)) {
          rows.push('<div class="bknj-cart-meta-line bknj-cart-meta-line--size">Size: ' + escapeHtml(value) + '</div>');
        } else {
          rows.push('<div class="bknj-cart-meta-line">' + escapeHtml(value) + '</div>');
        }
      });
    }

    if (!rows.length) {
      var variantTitle = item && item.variant_title && item.variant_title !== 'Default Title'
        ? String(item.variant_title)
        : '';

      if (variantTitle) {
        var parts = variantTitle.split(' / ').map(function(part){ return part.trim(); }).filter(Boolean);
        parts.forEach(function(part, partIndex){
          var looksLikeSize = /^(xxs|xs|s|m|l|xl|xxl|xxxl|\d+(?:\.\d+)?|\d+(?:\.\d+)?\s*(?:mm|cm|in))$/i.test(part);
          var isLast = partIndex === parts.length - 1;
          var labelAsSize = looksLikeSize && (parts.length > 1 || isLast);
          rows.push('<div class="bknj-cart-meta-line' + (labelAsSize ? ' bknj-cart-meta-line--size' : '') + '">' +
            (labelAsSize ? 'Size: ' : '') + escapeHtml(part) + '</div>');
        });
      }
    }

    return rows.join('');
  }

  function cartRowHtml(item, index, drawer){
    var title = escapeHtml(item.product_title || item.title || '');
    var image = item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + title + '" loading="eager" decoding="async">' : '';
    var key = escapeHtml(item.key || '');
    var line = index + 1;
    var finalLinePrice = Number(item.final_line_price || item.line_price || 0);
    var meta = cartMetaHtml(item);

    return '' +
      '<div class="bknj-cart-item" data-key="' + key + '" data-line="' + line + '">' +
        '<div class="bknj-cart-thumb">' + image + '</div>' +
        '<div class="bknj-cart-info">' +
          '<div class="bknj-cart-title">' + title + '</div>' +
          '<div class="bknj-cart-meta">' + meta + '</div>' +
          '<div class="bknj-cart-actions">' +
            '<div class="bknj-qtywrap">' +
              '<button class="bknj-qtybtn" type="button" data-qty="-1" aria-label="Decrease quantity">−</button>' +
              '<input class="bknj-cart-qty" type="number" min="0" value="' + Number(item.quantity || 0) + '" inputmode="numeric" pattern="[0-9]*">' +
              '<button class="bknj-qtybtn" type="button" data-qty="1" aria-label="Increase quantity">+</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="bknj-cart-price">' + formatMoney(finalLinePrice) + '</div>' +
        '<button class="bknj-cart-remove" type="button" aria-label="Remove item">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true">' +
            '<path d="M8 7h8M10 7V5h4v2M9 10v7M15 10v7M6 7l1 14h10l1-14"></path>' +
          '</svg>' +
        '</button>' +
      '</div>';
  }

  function updateBadges(cart){
    var qty = (cart.items || []).reduce(function(sum, item){
      return sum + Number(item.quantity || 0);
    }, 0);

    qsa(selectors.count).forEach(function(count){
      count.textContent = qty > 9 ? '9+' : String(qty);
      count.classList.toggle('is-visible', qty > 0);
    });

    qsa(selectors.titleCount).forEach(function(count){
      count.textContent = '(' + qty + ')';
    });

    qsa(selectors.subtotalCount).forEach(function(count){
      count.textContent = '(' + qty + ' ' + (qty === 1 ? 'item' : 'items') + ')';
    });

    return qty;
  }

  function removeViewBagButtons(drawer){
    if (!drawer) return;

    qsa('a, button, input[type="button"], input[type="submit"]', drawer).forEach(function(button){
      var text = (button.textContent || button.value || '').replace(/\s+/g, ' ').trim().toLowerCase();
      var href = (button.getAttribute('href') || '').split('?')[0].replace(/\/$/, '');

      var isCheckout = text.indexOf('checkout') >= 0 || text.indexOf('check out') >= 0 || href.indexOf('/checkout') >= 0;
      var isCartLink = href === '/cart' || href.indexOf('/cart') >= 0;
      var isViewBag =
        text === 'view bag' ||
        text === 'view cart' ||
        text.indexOf('view bag') >= 0 ||
        text.indexOf('view cart') >= 0 ||
        text.indexOf('view your shopping bag') >= 0 ||
        text.indexOf('view shopping bag') >= 0;

      if (!isCheckout && (isViewBag || isCartLink)) {
        var removable = closest(button, '.bknj-cart__btn, .previewCartAction > *, .cart-drawer__footer > *, .drawer__footer > *, .cart__footer > *, .button-group > *') || button;
        removable.remove();
      }
    });
  }


  function findCheckoutButton(drawer){
    if (!drawer) return null;

    var directMatches = qsa(selectors.checkout, drawer).filter(function(el){
      return true;
    });

    var visibleDirect = directMatches.find(function(el){
      return isElementVisible(el);
    });

    if (visibleDirect) return visibleDirect;
    if (directMatches.length) return directMatches[0];

    var textMatches = qsa('a, button, input[type="submit"]', drawer).filter(function(el){
      var text = (el.textContent || el.value || '').replace(/\s+/g, ' ').trim().toLowerCase();
      return text.indexOf('checkout') >= 0 || text.indexOf('check out') >= 0;
    });

    var visibleText = textMatches.find(function(el){
      return isElementVisible(el);
    });

    return visibleText || textMatches[0] || null;
  }

  function ensureCheckoutLabel(drawer){
    if (!drawer) return;

    var checkout = findCheckoutButton(drawer);
    if (!checkout) return;

    checkout.setAttribute('aria-label', 'Checkout');

    if (checkout.tagName && checkout.tagName.toLowerCase() === 'input') {
      checkout.value = 'CHECKOUT';
      return;
    }

    if (checkout.innerHTML === '<span>CHECKOUT</span>') return;
    checkout.innerHTML = '<span>CHECKOUT</span>';
  }

  function removeLegacyCartExtras(drawer){
    if (!drawer) return;

    qsa('[data-bknj-reserved-notice], .bknj-cart-express', drawer)
      .forEach(function(el){ el.remove(); });
  }

  function syncCartHeaderRow(drawer){
    if (!drawer) return;

    var header = qs('.bknj-cart__header', drawer);
    var titleCount = qs(selectors.titleCount, drawer);
    var closeButton = qs('.bknj-cart__close', drawer);
    if (!header || !titleCount || !closeButton) return;

    header.classList.add('bknj-cart-header-line');
    closeButton.classList.add('bknj-cart-header-close');
  }

  function syncCartFooter(drawer){
    drawer = drawer || getDrawer(true) || getDrawer(false);
    if (!drawer) return;

    removeViewBagButtons(drawer);
    removeLegacyCartExtras(drawer);
    ensureCheckoutLabel(drawer);
    syncCartHeaderRow(drawer);
  }

  function getProductContextFromPage(){
    var section = qs(selectors.productSection);
    var productId = section ? section.getAttribute('data-product-id') : '';
    return { productId: productId ? String(productId) : '' };
  }

  function renderCart(drawer, cart){
    if (!drawer || !cart) return;

    var itemsEl = qs(selectors.items, drawer);
    var items = cart.items || [];

    drawer.classList.remove('bknj-cart--single-main-item');

    if (itemsEl) {
      itemsEl.innerHTML = items.length
        ? items.map(function(item, index){ return cartRowHtml(item, index, drawer); }).join('')
        : '<div class="bknj-cart-empty">No items in your bag</div>';
    }

    var subtotalEl = qs(selectors.subtotal, drawer);
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.items_subtotal_price || cart.total_price || 0);
    }

    updateBadges(cart);
    bindCartItemControls(drawer);
    syncCartFooter(drawer);
  }

  function bindCartItemControls(drawer){
    if (!drawer || drawer.__bknjCartControlsBound) return;
    drawer.__bknjCartControlsBound = true;

    drawer.addEventListener('click', function(event){
      var row = closest(event.target, '.bknj-cart-item');
      if (!row) return;

      var line = Number(row.getAttribute('data-line') || 0);
      if (!line) return;

      var qtyBtn = closest(event.target, '[data-qty]');
      var removeBtn = closest(event.target, '.bknj-cart-remove');

      if (!qtyBtn && !removeBtn) return;

      event.preventDefault();

      var input = qs('.bknj-cart-qty', row);
      var current = input ? Number(input.value || 0) : 0;
      var next = removeBtn ? 0 : Math.max(0, current + Number(qtyBtn.getAttribute('data-qty') || 0));

      drawer.classList.add('is-busy');

      changeLine(line, next)
        .then(function(cart){
          renderCart(drawer, cart);
          emit('change', { cart: cart });
        })
        .finally(function(){
          drawer.classList.remove('is-busy');
        });
    });

    drawer.addEventListener('change', function(event){
      var input = closest(event.target, '.bknj-cart-qty');
      if (!input) return;

      var row = closest(input, '.bknj-cart-item');
      var line = row ? Number(row.getAttribute('data-line') || 0) : 0;
      if (!line) return;

      var next = Math.max(0, Number(input.value || 0));

      drawer.classList.add('is-busy');

      changeLine(line, next)
        .then(function(cart){
          renderCart(drawer, cart);
          emit('change', { cart: cart });
        })
        .finally(function(){
          drawer.classList.remove('is-busy');
        });
    });
  }

  function setDrawerOpen(drawer, open){
    if (!drawer) return;

    window.clearTimeout(drawer.__bknjCloseTimer);

    if (open) {
      /* Close the opposite desktop/mobile drawer first, without animation. */
      getAllDrawers().forEach(function(other){
        if (other === drawer) return;
        window.clearTimeout(other.__bknjCloseTimer);
        other.classList.remove('is-open', 'is-closing', 'bknj-overlay-closing');
        other.setAttribute('aria-hidden', 'true');
      });

      drawer.classList.remove('is-closing', 'bknj-overlay-closing');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
    } else {
      drawer.classList.remove('is-open', 'bknj-overlay-closing');
      drawer.classList.add('is-closing');
      drawer.setAttribute('aria-hidden', 'true');

      drawer.__bknjCloseTimer = window.setTimeout(function(){
        drawer.classList.remove('is-closing');
      }, 460);
    }

    /*
      Important:
      Do NOT add Ella/global popup classes here.
      Classes like bknj-blur-active / bknj-popup-lock can trigger theme-wide
      overlays that sit above the cart drawer and grey out the cart itself.
      The global cart owns only this lightweight lock class.
    */
    document.documentElement.classList.toggle('bknj-cart-open', open);
    document.body.classList.toggle('bknj-cart-open', open);

    if (!open) {
      document.documentElement.classList.remove('bknj-popup-lock');
      document.body.classList.remove('bknj-popup-lock', 'bknj-blur-active');
    }

    var header = closest(drawer, '#bknj-desktop-header, #bknj-mobile-header');
    if (header) {
      header.classList.toggle('bknj-header-overlay-open', open);
      if (!open) {
        header.classList.add('bknj-header-overlay-closing');
        window.setTimeout(function(){
          header.classList.remove('bknj-header-overlay-closing');
        }, 460);
      }
    }
  }

  function closeCart(drawer){
    drawer = drawer || state.activeDrawer || getDrawer(true);
    if (!drawer) return;

    setDrawerOpen(drawer, false);
    state.activeDrawer = null;
    emit('close', { drawer: drawer });
  }

  function openCart(options){
    options = options || {};
    var drawer = options.drawer || getDrawer(false);
    var requireItems = !!options.requireItems;
    var allowEmpty = !!options.allowEmpty;
    var forceFresh = !!options.forceFresh;
    var cached = getCachedCart();
    var cacheFresh = !forceFresh && isCartCacheFresh(15000);

    if (!drawer) return Promise.resolve(null);

    function openWithCart(cart, fromCache){
      if (requireItems && !allowEmpty && !cartHasItems(cart)) {
        if (state.activeDrawer) closeCart(state.activeDrawer);
        showEmptyBagMessage();
        return cart;
      }

      /* Render while the drawer is still hidden, then animate it in.
         This prevents products/subtotal from visibly appearing after the panel opens. */
      renderCart(drawer, cart);
      state.activeDrawer = drawer;
      setDrawerOpen(drawer, true);
      emit('open', { drawer: drawer, cart: cart, cached: !!fromCache });
      return cart;
    }

    /* Fresh cache = instant open with fully rendered content. */
    if (cached && cacheFresh) {
      if (requireItems && !allowEmpty && !cartHasItems(cached)) {
        showEmptyBagMessage();
        return Promise.resolve(cached);
      }
      return Promise.resolve(openWithCart(cached, true));
    }

    /* Stale/no cache = fetch first, render while hidden, then open.
       No blank drawer and no visible mid-animation cart rebuild. */
    return fetchCart()
      .then(function(cart){
        return openWithCart(cart, false);
      })
      .catch(function(error){
        emit('error', { error: error });
        throw error;
      })
      .finally(function(){
        window.clearTimeout(state.openTimer);
        state.openTimer = window.setTimeout(function(){
          syncCartFooter(drawer);
        }, 160);
      });
  }

  function refresh(){
    var drawer = state.activeDrawer || getDrawer(true) || getDrawer(false);

    return fetchCart().then(function(cart){
      getAllDrawers().forEach(function(possibleDrawer){
        if (possibleDrawer === drawer || possibleDrawer.getAttribute('aria-hidden') === 'false') {
          renderCart(possibleDrawer, cart);
        }
      });

      if (!drawer) updateBadges(cart);
      emit('refresh', { cart: cart, drawer: drawer });
      return cart;
    });
  }

  function bindOpenClose(){
    document.addEventListener('click', function(event){
      var opener = closest(event.target, selectors.openers);
      if (opener) {
        event.preventDefault();
        openCart({ requireItems: true });
        return;
      }

      var closer = closest(event.target, selectors.closers);
      if (closer) {
        event.preventDefault();
        closeCart(closest(closer, selectors.drawers));
        return;
      }

      var activeDrawer = state.activeDrawer || getDrawer(true);
      if (activeDrawer && activeDrawer.getAttribute('aria-hidden') === 'false') {
        var clickedInsideDrawer = closest(event.target, selectors.drawers) === activeDrawer;
        var clickedInsidePanel = !!closest(event.target, '.bknj-cart__panel');

        if (clickedInsideDrawer && !clickedInsidePanel) {
          event.preventDefault();
          closeCart(activeDrawer);
        }
      }
    });

    document.addEventListener('keydown', function(event){
      if (event.key === 'Escape') closeCart();
    });
  }

  function bindProductForms(){
    document.addEventListener('submit', function(event){
      var form = closest(event.target, selectors.productForm);
      if (!form) return;

      var bknjProductSection = closest(form, '[id^="BknjJhProduct-"], .bknj-jh-product');
      if (!bknjProductSection && !form.hasAttribute('data-bknj-global-cart-form')) return;

      event.preventDefault();

      var submitter = event.submitter || qs('[type="submit"], button[name="add"]', form);
      var message = qs('[data-bknj-form-message]', form.parentNode || form);
      var context = getProductContextFromPage();

      if (submitter) submitter.classList.add('is-loading');
      if (message) message.textContent = '';

      var giftOfferRoot = bknjProductSection ? qs('[data-bknj-merch-offer]', bknjProductSection) : null;
      var hasGiftOffer = !!(giftOfferRoot && qs('[data-bknj-offer-item][data-kind="gift"]', giftOfferRoot));
      var gatePromise = Promise.resolve({ allowed:true });

      if (hasGiftOffer) {
        if (window.BKNJGiftAddon && typeof window.BKNJGiftAddon.validateProductAdd === 'function') {
          gatePromise = window.BKNJGiftAddon.validateProductAdd(form);
        } else {
          // Fail closed if the gift UI exists but its controller has not loaded.
          gatePromise = Promise.resolve({
            allowed:false,
            message:'Please select your complimentary gift before adding to bag.'
          });
        }
      }

      gatePromise
        .then(function(gate){
          if (gate && gate.allowed === false) {
            if (message && (!window.BKNJGiftAddon || typeof window.BKNJGiftAddon.validateProductAdd !== 'function')) {
              message.textContent = gate.message || 'Please select your complimentary gift before adding to bag.';
              message.className = 'bknj-jh-form-message is-error';
            }
            return null;
          }
          return addForm(form);
        })
        .then(function(item){
          if (!item) return null;
          var sourceId = context.productId || item.product_id || '';
          var hookContext = { item: item, sourceId: sourceId, form: form };
          emit('add', hookContext);
          return runAfterProductAddHooks(hookContext).then(function(){
            return openCart({ allowEmpty: true, forceFresh: true });
          });
        })
        .catch(function(error){
          var text = error && error.description ? error.description : 'Something went wrong. Please try again.';
          if (message) {
            message.textContent = text;
            message.className = 'bknj-jh-form-message is-error';
          }
          emit('error', { error: error });
        })
        .finally(function(){
          if (submitter) submitter.classList.remove('is-loading');
        });
    }, true);
  }



  function init(){
    bindOpenClose();
    bindProductForms();

    /* Warm the cart cache immediately and render BOTH hidden drawers up front.
       Cart thumbnails use eager loading, so the drawer is visually ready before a click. */
    fetchCart()
      .then(function(cart){
        updateBadges(cart);
        getAllDrawers().forEach(function(drawer){
          renderCart(drawer, cart);
        });
      })
      .catch(function(){});


    emit('ready', { version: BKNJCart.version });
  }

  BKNJCart.open = openCart;
  BKNJCart.close = closeCart;
  BKNJCart.refresh = refresh;
  BKNJCart.fetch = fetchCart;
  BKNJCart.addForm = addForm;
  BKNJCart.render = renderCart;
  BKNJCart.syncCartFooter = syncCartFooter;
  BKNJCart.registerAfterProductAddHook = registerAfterProductAddHook;
  BKNJCart.selectors = selectors;

  window.BKNJCart = BKNJCart;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(window, document);
